import React, { useEffect, useState } from 'react';

import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import InvoiceTable from '../../../components/tables/InvoiceTable';
import { INVOICE_STATUS_OPTIONS } from '../../../constants/InvoiceStatusConstants';
import {
  useInvoiceSearch,
  useMarkInvoiceAsPaid,
} from '../../../hooks/useInvoiceApi';
import { InvoiceDTO } from '../../../services/apiClient';

const InvoiceList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({});
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<InvoiceDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useInvoiceSearch();
  const markAsPaid = useMarkInvoiceAsPaid();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoices', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleMarkAsPaid = async (dto: InvoiceDTO) => {
    try {
      await markAsPaid.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/invoices"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Invoice.View"
    >
      <Card title="Invoices" subtitle={`Total Records: ${totalRecords}`}>
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="invoiceNumber"
              label="Search by Invoice Number"
              value={formFilters.invoiceNumber || ''}
              onChange={(e) =>
                setFormFilters({
                  ...formFilters,
                  invoiceNumber: e.target.value,
                })
              }
              placeholder="Search by Invoice Number"
            />
            <FormField
              id="invoiceStatusId"
              label="Status"
              type="dropdown"
              value={formFilters.invoiceStatusId || ''}
              onChange={(e) =>
                setFormFilters({
                  ...formFilters,
                  invoiceStatusId: e.target.value,
                })
              }
              options={[{ value: '', label: 'All' }, ...INVOICE_STATUS_OPTIONS]}
              placeholder="All"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <InvoiceTable
          data={items}
          handleMarkAsPaid={handleMarkAsPaid}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default InvoiceList;
